import i18n from 'i18next'
import {
  IParsedSkill,
  IParsedCombinedWorkHistory,
  IParsedCombinedProject,
  IParsedCombinedEducation,
  IParsedCombinedCertificate,
  IParsedCombinedCourse,
  IParsedCombinedReference,
  IParsedCombinedLink,
  IParsedCombinedCompetence,
} from 'types/cvParserInterfaces'

/**
 * Validation errors for skills
 */
export interface ISkillValidationErrors extends Record<string, string | undefined> {
  name?: string
  level?: string
}

/**
 * Validation errors for work history
 */
export interface IWorkHistoryValidationErrors extends Record<string, string | undefined> {
  name?: string
  role?: string
  description?: string
  startDate?: string
  endDate?: string
}

/**
 * Validation errors for projects
 */
export interface IProjectValidationErrors extends Record<string, string | undefined> {
  name?: string
  customer?: string
  employer?: string
  role?: string
  description?: string
  startDate?: string
  endDate?: string
}

/**
 * Validation errors for education
 */
export interface IEducationValidationErrors extends Record<string, string | undefined> {
  degree?: string
  school?: string
  description?: string
  startDate?: string
  endDate?: string
}

/**
 * Validation errors for certificates
 */
export interface ICertificateValidationErrors extends Record<string, string | undefined> {
  name?: string
  description?: string
  issueDate?: string
  endDate?: string
}

/**
 * Validation errors for competences
 */
export interface ICompetenceValidationErrors extends Record<string, string | undefined> {
  name?: string
  issuer?: string
}

/**
 * Validation errors for courses
 */
export interface ICourseValidationErrors extends Record<string, string | undefined> {
  name?: string
  description?: string
  startDate?: string
  endDate?: string
}

/**
 * Validation errors for references
 */
export interface IReferenceValidationErrors extends Record<string, string | undefined> {
  name?: string
  description?: string
}

/**
 * Validation errors for links
 */
export interface ILinkValidationErrors extends Record<string, string | undefined> {
  url?: string
  title?: string
}

/**
 * Validates a skill
 */
export const validateSkill = (skill: IParsedSkill): ISkillValidationErrors => {
  const errors: ISkillValidationErrors = {}

  if (!skill.name || skill.name.trim().length === 0) {
    errors.name = i18n.t('fieldRequired')
  }

  return errors
}

/**
 * Validates work history
 */
export const validateWorkHistory = (
  workHistory: IParsedCombinedWorkHistory,
  selectedLanguageId: number
): IWorkHistoryValidationErrors => {
  const errors: IWorkHistoryValidationErrors = {}
  const currentTranslation = workHistory.translations.find(tr => tr.languageId === selectedLanguageId)

  if (!currentTranslation?.name || currentTranslation.name.trim().length === 0) {
    errors.name = i18n.t('fieldRequired')
  }

  if (!currentTranslation?.description || currentTranslation.description.trim().length === 0) {
    errors.description = i18n.t('fieldRequired')
  }

  if (workHistory.startDate && workHistory.endDate) {
    if (new Date(workHistory.startDate) > new Date(workHistory.endDate)) {
      errors.startDate = i18n.t('error.dateRange')
    }
  }

  return errors
}

/**
 * Validates project
 */
export const validateProject = (
  project: IParsedCombinedProject,
  selectedLanguageId: number
): IProjectValidationErrors => {
  const errors: IProjectValidationErrors = {}
  const currentTranslation = project.translations.find(tr => tr.languageId === selectedLanguageId)

  if (!currentTranslation?.name || currentTranslation.name.trim().length === 0) {
    errors.name = i18n.t('fieldRequired')
  }

  if (!currentTranslation?.description || currentTranslation.description.trim().length === 0) {
    errors.description = i18n.t('fieldRequired')
  }

  if (project.startDate && project.endDate) {
    if (new Date(project.startDate) > new Date(project.endDate)) {
      errors.startDate = i18n.t('error.dateRange')
    }
  }

  return errors
}

/**
 * Validates education
 */
export const validateEducation = (
  education: IParsedCombinedEducation,
  selectedLanguageId: number
): IEducationValidationErrors => {
  const errors: IEducationValidationErrors = {}
  const currentTranslation = education.translations.find(tr => tr.languageId === selectedLanguageId)

  if (!currentTranslation?.degree || currentTranslation.degree.trim().length === 0) {
    errors.degree = i18n.t('fieldRequired')
  }

  if (!currentTranslation?.school || currentTranslation.school.trim().length === 0) {
    errors.school = i18n.t('fieldRequired')
  }

  if (education.startDate && education.endDate) {
    if (new Date(education.startDate) > new Date(education.endDate)) {
      errors.startDate = i18n.t('error.dateRange')
    }
  }

  return errors
}

/**
 * Validates certificate
 */
export const validateCertificate = (
  certificate: IParsedCombinedCertificate,
  selectedLanguageId: number
): ICertificateValidationErrors => {
  const errors: ICertificateValidationErrors = {}
  const currentTranslation = certificate.translations.find(tr => tr.languageId === selectedLanguageId)

  if (!currentTranslation?.name || currentTranslation.name.trim().length === 0) {
    errors.name = i18n.t('fieldRequired')
  }

  if (certificate.issueDate && certificate.expirationDate) {
    if (new Date(certificate.issueDate) > new Date(certificate.expirationDate)) {
      errors.issueDate = i18n.t('error.dateRange')
    }
  }

  return errors
}

/**
 * Validates competence: like on the profile, the name and the issuer are required
 */
export const validateCompetence = (
  competence: IParsedCombinedCompetence,
  selectedLanguageId: number
): ICompetenceValidationErrors => {
  const errors: ICompetenceValidationErrors = {}
  const currentTranslation = competence.translations.find(tr => tr.languageId === selectedLanguageId)

  if (!currentTranslation?.name || currentTranslation.name.trim().length === 0) {
    errors.name = i18n.t('fieldRequired')
  }

  if (!currentTranslation?.issuer || currentTranslation.issuer.trim().length === 0) {
    errors.issuer = i18n.t('fieldRequired')
  }

  return errors
}

/**
 * Validates course
 */
export const validateCourse = (course: IParsedCombinedCourse, selectedLanguageId: number): ICourseValidationErrors => {
  const errors: ICourseValidationErrors = {}
  const currentTranslation = course.translations.find(tr => tr.languageId === selectedLanguageId)

  if (!currentTranslation?.name || currentTranslation.name.trim().length === 0) {
    errors.name = i18n.t('fieldRequired')
  }

  if (course.startDate && course.endDate) {
    if (new Date(course.startDate) > new Date(course.endDate)) {
      errors.startDate = i18n.t('error.dateRange')
    }
  }

  return errors
}

/**
 * Validates reference
 */
export const validateReference = (reference: IParsedCombinedReference): IReferenceValidationErrors => {
  const errors: IReferenceValidationErrors = {}

  if (!reference.firstName || reference.firstName.trim().length === 0) {
    errors.name = i18n.t('fieldRequired')
  }

  if (!reference.lastName || reference.lastName.trim().length === 0) {
    errors.name = i18n.t('fieldRequired')
  }

  return errors
}

/**
 * Validates link
 */
export const validateLink = (link: IParsedCombinedLink): ILinkValidationErrors => {
  const errors: ILinkValidationErrors = {}

  if (!link.url || link.url.trim().length === 0) {
    errors.url = i18n.t('fieldRequired')
  }

  // Validate URL format
  if (link.url) {
    try {
      new URL(link.url)
    } catch {
      errors.url = i18n.t('invalidUrl')
    }
  }

  return errors
}

/**
 * Check if there are any validation errors
 */
export const hasValidationErrors = (errors: Record<string, string | undefined>): boolean => {
  return Object.values(errors).some(error => !!error)
}
